import { useMemo, useState } from 'react'
import { Avatar, Divider, Flex } from 'antd'
import { FileImageOutlined } from '@ant-design/icons'
import cn from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import Text from 'shared/components/typography/Text'
import { getSrcSet } from 'shared/utils/assets'
import ChooseImageModal from 'shared/components/ChooseImageModal'
import { ImageAsset } from 'shared/types/assets'
import Collapse from 'shared/components/Collapse'
import { lang } from '../lang'
import s from '../s.module.css'
import { lang as commonLang } from '../../lang'
import { MediaPriceLimit } from '../../hooks/useMediaPricesLimits'
import CollapseHeader from '../CollapseHeader'
import ImageIcon from './image.svg?react'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'
import { ICurrency } from '@/pages/Donation/api/types'
import { CustomizeType } from '@/pages/Donation/types'
import PayToUploadButton from '@/pages/Donation/components/PayToUploadButton'

type Props = {
  baseCurrency: ICurrency | null
  mediaPriceLimit?: MediaPriceLimit
  isSelected: boolean
  selectedImageId: string | null
  onSelectImage: (value: string) => void
  onChangeCustomizeType: (type: CustomizeType[number]) => void
  handleClose: (type: CustomizeType[number]) => void
  onAddAmount: (type: CustomizeType[number]) => void
}

export default function Image({
  baseCurrency,
  mediaPriceLimit,
  isSelected,
  selectedImageId,
  onSelectImage,
  onChangeCustomizeType,
  onAddAmount,
  handleClose,
}: Props) {
  const intl = useIntl()
  const [isImageSelectOpen, setImageSelectOpen] = useState(false)
  const { config } = useStreamerConfigContext()
  const assets = config?.image_donation_config?.assets

  const images = useMemo(() => {
    if (!assets) {
      return []
    }

    return assets.map<ImageAsset>(item => ({
      id: item.id,
      url: item.url,
      srcSet: getSrcSet(item.thumbnails),
      preview_url: item.preview_url,
      is_default: item.is_default,
    }))
  }, [assets])

  const selectedAsset = useMemo(() => {
    if (!selectedImageId) {
      return
    }

    return images?.find(item => item.id === selectedImageId)
  }, [images, selectedImageId])

  const closeModal = () => setImageSelectOpen(false)

  const handleChooseImage = (img: ImageAsset | null) => {
    if (img) {
      onSelectImage(img.id)
    }

    closeModal()
  }

  const shouldShowChangeButton = !!(isSelected && images.length && selectedImageId)
  const isClickable = !!((!isSelected || !selectedAsset) && mediaPriceLimit == null)
  const shouldShowCloseButton = !!(isSelected && selectedAsset)

  const handleCustomizeImage = () => {
    if (!isClickable) {
      return
    }

    onChangeCustomizeType('image')
    setImageSelectOpen(true)
  }

  return (
    <>
      <ChooseImageModal
        isOpen={isImageSelectOpen}
        onCancel={closeModal}
        imagesList={images}
        onSelectImage={handleChooseImage}
        selectedImage={selectedImageId}
        title={<FormattedMessage {...lang.chooseImageTitle} />}
        subtitle={<FormattedMessage {...lang.chooseImageSubtitle} />}
      />

      <Flex
        className={cn(s.wrap, {
          [s.clickable]: isClickable,
        })}
        vertical
        onClick={handleCustomizeImage}
      >
        <CollapseHeader
          icon={<ImageIcon />}
          title={intl.formatMessage(lang.addImage)}
          shouldShowChangeButton={shouldShowChangeButton}
          shouldShowCloseButton={shouldShowCloseButton}
          shouldShowLimit={mediaPriceLimit != null}
          onChange={() => setImageSelectOpen(true)}
          onClose={() => handleClose('image')}
          limit={
            <Text>
              <FormattedMessage
                {...commonLang.payToAdd}
                values={{
                  type: intl.formatMessage(lang.image),
                  action: (
                    <PayToUploadButton
                      amount={Number(mediaPriceLimit?.payAmount)}
                      currencyCode={baseCurrency?.code}
                      onClick={() => {
                        onAddAmount('image')
                        setImageSelectOpen(true)
                      }}
                    />
                  ),
                }}
              />
            </Text>
          }
        />
        <Collapse isOpen={!!selectedImageId}>
          <div>
            <Divider className={s.divider} />
            <Flex justify="center">
              {selectedAsset ? (
                <Avatar
                  src={selectedAsset.url}
                  srcSet={selectedAsset.srcSet}
                  shape="square"
                  size={84}
                  alt="donation image"
                  icon={<FileImageOutlined style={{ width: '24px' }} />}
                />
              ) : (
                <span className={s.imagePlaceholder} />
              )}
            </Flex>
          </div>
        </Collapse>
      </Flex>
    </>
  )
}
